import type {
    GridApiPremium,
    GridColDef,
    GridColumnVisibilityModel,
    GridValidRowModel,
    MuiEvent
} from '@mui/x-data-grid-premium';
import type { RefObject } from 'react';
import type { ColumnVisibilityPreset } from '../ColumnVisibiltyUtills';
import { useColumnVisibility } from '../useColumnVisibilty';
import type { GridSettingsPreference } from './gridSettings/types';
import { useDataGridColumnOrder } from './useDataGridColumnOrder';
import { useDataGridColumnViews } from './useDataGridColumnViews';

type UseDataGridColumnViewFeatureParams = {
    enabled: boolean;
    gridId: string;
    apiRef: RefObject<GridApiPremium | null>;
    columns: GridColDef[];
    rows: GridValidRowModel[];
    sizedColumns: GridColDef[];
    onColumnOrderChange?: (allColumns: GridColDef[], event: MuiEvent<{}>) => void;
    presetsLabelToken?: string;
    defaultColumnVisibility: GridColumnVisibilityModel;
    columnVisibilityPresets: ColumnVisibilityPreset[];
    defaultSelectedPreset?: number;
    enabledFavouritePreset?: boolean;
    hideMyColumnsPreset?: boolean;
    defaultSelectedPresetLabel?: string;
    hideAllColumnsPreset?: boolean;
    hideDefaultColumnsPreset?: boolean;
    /** When true, column order is saved on the view and restored with it. */
    trackColumnOrder?: boolean;
    /** When true, showing or hiding columns marks the view dirty. */
    trackColumnVisibility?: boolean;
    getCurrentGridSettings?: () => GridSettingsPreference | undefined;
    applyGridSettings?: (next: GridSettingsPreference | null) => void;
    defaultGridSettings?: GridSettingsPreference;
    gridSettingsRevision?: number;
};

/**
 * Single DataGrid entry for saved column views + column order.
 *
 * Internally: prefs (`useDataGridColumnViews`) → visibility (`useColumnVisibility`) →
 * MUI order (`useDataGridColumnOrder`), with a ref bridge because order apply/get
 * must be available to visibility before the order hook returns.
 *
 * Call after `sizedColumns` is available. When `enabled` is false, save/order paths
 * are no-ops and `onColumnOrderChange` is forwarded unchanged.
 */
export function useDataGridColumnViewFeature({
    enabled,
    gridId,
    apiRef,
    columns,
    rows,
    sizedColumns,
    onColumnOrderChange,
    presetsLabelToken,
    defaultColumnVisibility,
    columnVisibilityPresets,
    defaultSelectedPreset,
    enabledFavouritePreset = false,
    hideMyColumnsPreset = false,
    defaultSelectedPresetLabel,
    hideAllColumnsPreset,
    hideDefaultColumnsPreset,
    trackColumnOrder = false,
    trackColumnVisibility = false,
    getCurrentGridSettings,
    applyGridSettings,
    defaultGridSettings,
    gridSettingsRevision
}: UseDataGridColumnViewFeatureParams) {
    const { visibilityOptions, syncFromVisibility, bindColumnOrderApi, setColumnOrderDirty, bumpColumnOrderRevision } =
        useDataGridColumnViews({
            enabled,
            gridId,
            columns,
            trackColumnOrder,
            trackColumnVisibility,
            getCurrentGridSettings
        });

    const {
        columnVisibiltyModel,
        setColumnVisibiltyModel,
        selectedPresetLabel,
        selectedSavedViewId,
        ColumnVisibilitySelector
    } = useColumnVisibility(
        gridId,
        presetsLabelToken,
        defaultColumnVisibility,
        columnVisibilityPresets,
        defaultSelectedPreset,
        enabledFavouritePreset,
        hideMyColumnsPreset,
        defaultSelectedPresetLabel,
        hideAllColumnsPreset,
        hideDefaultColumnsPreset,
        {
            ...visibilityOptions,
            getCurrentGridSettings,
            defaultGridSettings,
            gridSettingsRevision,
            onGridSettingsApplied: applyGridSettings
        }
    );

    syncFromVisibility(columnVisibiltyModel, selectedSavedViewId ?? null);

    const { handleColumnOrderChange, applyOrderedFieldsToGrid, getCurrentOrderedFields } = useDataGridColumnOrder({
        enabled: trackColumnOrder,
        apiRef,
        columns,
        rows,
        sizedColumns,
        columnVisibilityModel: columnVisibiltyModel,
        onColumnOrderChange,
        setColumnOrderDirty,
        bumpColumnOrderRevision
    });

    bindColumnOrderApi({ applyOrderedFieldsToGrid, getCurrentOrderedFields });

    return {
        columnVisibiltyModel,
        setColumnVisibiltyModel,
        selectedPresetLabel,
        selectedSavedViewId,
        ColumnVisibilitySelector,
        handleColumnOrderChange
    };
}
